import dayjs from 'dayjs';
import { ICloneable } from '../../../contracts/cloneable.js';
import { IDataComparable } from '../../../contracts/data-comparable.js';
import { IDiagnosticableObject } from '../../../contracts/diagnosticable-object.js';
import { kAppointmentTimeIncrement } from '../../../utility/constants.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../../utility/null-safety.js';
import { isSameData, isSameList, SerializedData } from '../../dto/dto.js';
import { TimeOfDay } from '../time-of-day.js';
import { CalendarDaySlot } from './calendar-day-slot.js';
import { SingleEntryCalendarSlot } from './single-entry-calendar-slot.js';

export class CalendarDayState implements ICloneable, IDataComparable, IDiagnosticableObject {
  private readonly calendarState: Map<number, CalendarDaySlot[]> = new Map<number, CalendarDaySlot[]>();

  constructor(
    public readonly uniqueId: string,
    public readonly slots: CalendarDaySlot[],
  ) {
    for(const slot of slots) {
      this.addSlotToCalendarState(slot);
    }
  }

  static empty(uniqueId: string): CalendarDayState {
    return new CalendarDayState(uniqueId, []);
  }

  static fromLegacyCalendarState(uniqueId: string, calendarState: Map<number, string[]>): CalendarDayState {
    const orderedKeys = [...calendarState.keys()];
    orderedKeys.sort();

    const startTimes = new Map<string, number>();
    const endTimes = new Map<string, number>();
    const entryIds = new Set<string>();

    for(const key of orderedKeys) {
      const entries = calendarState.get(key);
      if(isNotNullOrUndefined(entries)) {
        for(const entryId of entries) {
          if(!entryIds.has(entryId)) {
            entryIds.add(entryId);
            startTimes.set(entryId, key * kAppointmentTimeIncrement);
          }

          endTimes.set(entryId, key * kAppointmentTimeIncrement);
        }
      }
    }

    const slots = [...entryIds].map((entryId) => {
      const startMinute = startTimes.get(entryId);
      const endMinute = endTimes.get(entryId);

      if(isNullOrUndefined(startMinute)) {
        throw Error(`Failed to find start time for entry: ${entryId}`);
      }

      if(isNullOrUndefined(endMinute)) {
        throw Error(`Failed to find end time for entry: ${entryId}`);
      }

      return new SingleEntryCalendarSlot(
          entryId,
          TimeOfDay.fromMinutes(startMinute),
          dayjs.duration({
            minutes: endMinute - (startMinute - kAppointmentTimeIncrement),
          }),
      );
    });

    return new CalendarDayState(uniqueId, slots);
  }

  clone(): this {
    return new CalendarDayState(
        this.uniqueId,
        this.slots.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameList(other.slots, this.slots, (a, b) => isSameData(a, b)) &&
        other.uniqueId === this.uniqueId;
  }

  getSlots(minute: number): CalendarDaySlot[] {
    const key = Math.floor(minute / kAppointmentTimeIncrement);
    return this.calendarState.get(key) ?? [];
  }

  addSlot(slot: CalendarDaySlot): void {
    this.slots.push(slot);
    this.addSlotToCalendarState(slot);
  }

  removeSlot(index: number): void {
    if(index >= 0 && index < this.slots.length) {
      const slot = this.slots.splice(index, 1)[0];
      if(slot) {
        this.removeSlotFromCalendarState(slot);
      }
    }
  }

  private addSlotToCalendarState(slot: CalendarDaySlot): void {
    const startMinute = slot.timeOfDay.inMinutes;
    const duration = slot.duration.asMinutes();

    for(let minute = 0; minute < duration; minute += kAppointmentTimeIncrement) {
      const key = Math.floor(((startMinute + minute) / kAppointmentTimeIncrement));
      const slots = this.calendarState.get(key) ?? [];

      slots.push(slot);
      this.calendarState.set(key, slots);
    }
  }

  private removeSlotFromCalendarState(slotToRemove: CalendarDaySlot): void {
    const startMinute = slotToRemove.timeOfDay.inMinutes;
    const duration = slotToRemove.duration.asMinutes();

    for(let minute = 0; minute < duration; minute += kAppointmentTimeIncrement) {
      const key = Math.floor((startMinute + minute) / kAppointmentTimeIncrement);
      const slots = this.calendarState.get(key);

      if(isNotNullOrUndefined(slots)) {
        this.calendarState.set(key, slots.filter((slot) => slot.entryId !== slotToRemove.entryId));
      }
    }
  }

  toDiagnosticsObject(): SerializedData[] {
    return this.slots.map((e) => e.toDiagnosticsObject());
  }

  toLegacyCalendarState(): Map<number, CalendarDaySlot[]> {
    const calendarState = new Map<number, CalendarDaySlot[]>();

    for(const slot of this.slots) {
      const startMinute = slot.timeOfDay.inMinutes;
      const duration = slot.duration.asMinutes();

      for(let minute = 0; minute < duration; minute += kAppointmentTimeIncrement) {
        const key = Math.floor((startMinute + minute) / kAppointmentTimeIncrement);
        const slots = calendarState.get(key) ?? [];

        slots.push(slot);
        calendarState.set(key, slots);
      }
    }

    return calendarState;
  }
}
